import { ZENDESK_INTEGRATIONS_TITLE } from "@tellescope/constants";
import { i18n } from "@tellescope/i18n";
import {
  Bell,
  CalendarClock,
  CalendarX,
  ListChecks,
  LucideIcon,
  Mail,
  MessageCircle,
  MessagesSquare,
  Smartphone,
  Ticket,
  User,
  Users,
  Voicemail,
} from "lucide-react";
import { getZendeskSrc } from "../utils";
import { Location } from "react-router-dom";

export interface NotificationConfig {
  id: string;
  defaultValue: boolean;
  implemented: boolean;
  label: string;
  icon: LucideIcon | { getSrc: (location: Location) => string };
}

export interface NotificationConfigSection {
  sectionTitle: string;
  settings: NotificationConfig[];
}

export const getNotificationsConfig = () =>
  [
    {
      sectionTitle: i18n.t("Messaging", { ns: "notifications" }),
      settings: [
        {
          id: "newChatMessage",
          defaultValue: true,
          implemented: true,
          label: i18n.t("Chats", { ns: "notifications" }),
          icon: MessageCircle,
        },
        {
          id: "newEmail",
          defaultValue: true,
          implemented: true,
          label: i18n.t("Email", { ns: "notifications" }),
          icon: Mail,
        },
        {
          id: "newSMS",
          defaultValue: true,
          implemented: true,
          label: i18n.t("SMS", { ns: "notifications" }),
          icon: Smartphone,
        },
        {
          id: "newGroupMMS",
          defaultValue: true,
          implemented: true,
          label: i18n.t("Group MMS", { ns: "notifications" }),
          icon: MessagesSquare,
        },
      ],
    },
    {
      sectionTitle: i18n.t("Team chat", { ns: "notifications" }),
      settings: [
        {
          id: "newTeamChatMessage",
          defaultValue: true,
          implemented: true,
          label: i18n.t("New team chat", { ns: "notifications" }),
          icon: Users,
        },
      ],
    },
    {
      sectionTitle: i18n.t("Phone", { ns: "notifications" }),
      settings: [
        {
          id: "newVoicemail",
          defaultValue: true,
          implemented: true,
          label: i18n.t("Voicemail", { ns: "notifications" }),
          icon: Voicemail,
        },
      ],
    },
    {
      sectionTitle: i18n.t("Tickets", { ns: "notifications" }),
      settings: [
        {
          id: "newTicket",
          defaultValue: true,
          implemented: true,
          label: i18n.t("New ticket (assigned to anyone)", { ns: "notifications" }),
          icon: Ticket,
        },
        {
          id: "ticketCompletedCareTeam",
          defaultValue: false,
          implemented: true,
          label: i18n.t("New ticket (assigned to you)", { ns: "notifications" }),
          icon: User,
        },
        {
          id: "assignedTicket",
          defaultValue: true,
          implemented: true,
          label: i18n.t("Ticket assigned to you", { ns: "notifications" }),
          icon: Ticket,
        },
        {
          id: ZENDESK_INTEGRATIONS_TITLE,
          defaultValue: true,
          implemented: true,
          label: i18n.t("New Zendesk ticket comment", { ns: "notifications" }),
          icon: { getSrc: getZendeskSrc },
        },
      ],
    },
    {
      sectionTitle: i18n.t("Appointments", { ns: "notifications" }),
      settings: [
        {
          id: "appointmentCancellation",
          defaultValue: true,
          implemented: false,
          label: i18n.t("Appointment Cancelled", { ns: "notifications" }),
          icon: CalendarX,
        },
        {
          id: "appointmentReschedule",
          defaultValue: true,
          implemented: false,
          label: i18n.t("Appointment Rescheduled", { ns: "notifications" }),
          icon: CalendarClock,
        },
        {
          id: "appointmentReminder",
          defaultValue: true,
          implemented: false,
          label: i18n.t("Appointment Reminders", { ns: "notifications" }),
          icon: Bell,
        },
      ],
    },
    {
      sectionTitle: i18n.t("Vitals", { ns: "notifications" }),
      settings: [
        {
          id: "newLabResult",
          defaultValue: false,
          implemented: true,
          label: i18n.t("New laboratory result available", { ns: "notifications" }),
          icon: ListChecks,
        },
      ],
    },
  ] as const satisfies NotificationConfigSection[];

export const IMPLEMENTED_NOTIFICATION_CONFIGS = getNotificationsConfig().reduce<NotificationConfigSection[]>(
  (acc, curr) => {
    const filteredSection = {
      ...curr,
      settings: curr.settings.filter(({ implemented }) => implemented),
    };

    if (filteredSection.settings.length) {
      acc.push(filteredSection);
    }
    return acc;
  },
  []
);

export type ImplementedNotificationId = ReturnType<typeof getNotificationsConfig>[number]["settings"][number]["id"];
export const IMPLEMENTED_NOTIFICATION_IDS = IMPLEMENTED_NOTIFICATION_CONFIGS.flatMap<ImplementedNotificationId>(
  ({ settings }) => settings.map(({ id }) => id as ImplementedNotificationId)
);
